/**
 * WorkspaceUpgradeCTA
 *
 * Single source of truth for "should I show the Upgrade CTA here?"
 * and "if I do, which transition mode should it open in?".
 *
 * All five historical entry points route through these helpers so the
 * visibility rules stay consistent:
 *
 *   - SettingsPage sidebar "Upgrade Plan" button
 *   - SettingsPageController.showUpgradeToTeam
 *   - SettingsPage2Controller.upgradePlan
 *   - HomeMenuPanelController.showCreateWorkspace
 *   - HomeMenuPanelController.showUpgradeToPaidSubscriptionPopup
 *   - WorkspaceSwitcher "+ New workspace"
 */

import LocalStorageHelper from "models/helper/LocalStorageHelper";

function _normalizePlanType(planType) {
    if (!planType || typeof planType !== 'string') {
        return null;
    }
    return planType.trim().toLowerCase();
}

function _workspaceHasStripeSubscription(workspace) {
    return !!workspace && (
        workspace.hasStripeSubscription === true ||
        (workspace.billing && workspace.billing.hasStripeSubscription === true) ||
        (workspace.plan && workspace.plan.hasStripeSubscription === true)
    );
}

function _isDefaultPersonalWorkspace(workspace) {
    return !!workspace && (
        workspace.isDefaultPersonalWorkspace === true ||
        workspace.isDefaultWorkspace === true
    );
}

function _getOwnedWorkspaceByPlanPredicate(lsh, predicate) {
    var context = lsh.getWorkspaceContext();
    var contextRole = context && (context.myRole || context.role);
    if (context && contextRole === 'owner' && predicate(_normalizePlanType(context.planType), context)) {
        return context;
    }

    var workspaceList = [];
    try {
        workspaceList = lsh.getWorkspaceList() || [];
    } catch (error) {
        workspaceList = [];
    }

    for (var i = 0; i < workspaceList.length; i++) {
        var workspace = workspaceList[i];
        var workspaceRole = workspace && (workspace.myRole || workspace.role);
        if (!workspace || workspaceRole !== 'owner') {
            continue;
        }
        if (predicate(_normalizePlanType(workspace.planType), workspace)) {
            return workspace;
        }
    }

    return null;
}

/**
 * Should the Upgrade CTA be visible for the current user?
 *
 * Hide it when the user is already a paid workspace owner — they do not
 * need "Upgrade Plan" anymore; if they want another workspace they use
 * Create Workspace, which is governed by canUserCreateWorkspace.
 *
 * @param {Object} [userInfo] - optional pre-fetched LocalStorage user
 *   info. Falls back to LocalStorageHelper.getUserInfo() when omitted.
 * @returns {Boolean}
 */
export function shouldShowUpgradeCTA(userInfo) {
    var lsh = LocalStorageHelper.getInstance();
    var paidOwnedWorkspace = _getOwnedWorkspaceByPlanPredicate(lsh, function(planType, workspace) {
        return !!planType && planType !== 'free' && _workspaceHasStripeSubscription(workspace);
    });
    if (paidOwnedWorkspace) {
        return false;
    }

    // Free owners and non-paying members still upgrade their personal
    // workspace in place, even if they are currently viewing someone
    // else's workspace or browsing from personal space.
    return true;
}

/**
 * Which transition mode should the Upgrade popup open in for the current
 * user? Returns 'upgrade_in_place' when the user already owns a Free
 * workspace (the Free row must transform in place), otherwise 'create'.
 *
 * @param {Object} [userInfo]
 * @returns {'create'|'upgrade_in_place'}
 */
export function getRecommendedUpgradeMode(userInfo) {
    var lsh = LocalStorageHelper.getInstance();
    var inPlaceWorkspace = _getOwnedWorkspaceByPlanPredicate(lsh, function(planType, workspace) {
        return planType === 'free' || (
            _isDefaultPersonalWorkspace(workspace) &&
            !_workspaceHasStripeSubscription(workspace)
        );
    });

    // In-place also covers legacy/AppSumo/lifetime default-personal rows
    // whose planType looks paid but which do not have a workspace Stripe
    // subscription. Creating a second workspace from those rows is the bug.
    if (inPlaceWorkspace) {
        return 'upgrade_in_place';
    }
    return 'create';
}

export default {
    shouldShowUpgradeCTA: shouldShowUpgradeCTA,
    getRecommendedUpgradeMode: getRecommendedUpgradeMode,
};
